import {
    api,
    apiErrorMessage,
    formatMoney,
    ListBody,
    listSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    MoneyInput,
    PageHeader,
    Switch,
    UnsavedGuard,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { ApprovalRule } from '@/api/types';
import { P } from '@/lib/permissions';
import { useList, useWrite } from '@/lib/queries';

/** The reads of `/approvals/rules` (BM-46 §2.0). */
export const approvalRulesReads = () => ({
    list: listSpec('/staff/approval-rules'),
});

export function ApprovalRulesPage() {
    const can = useAuthStore((s) => s.can);
    // BM-105: the thresholds are the board's own (the platform's on the platform host, the tenant's on its board)
    const editable = can(P.approvalRulesManage);
    const query = useList<ApprovalRule>(approvalRulesReads().list.path);
    const [drafts, setDrafts] = useState<Record<string, number | null>>({});
    const [error, setError] = useState<string | null>(null);
    const put = useWrite(
        async (body: {
            rule: ApprovalRule;
            threshold_cents: number | null;
            enabled: boolean;
        }) =>
            (
                await api().put<ApprovalRule>(
                    `/staff/approval-rules/${body.rule.action}`,
                    {
                        threshold_cents: body.threshold_cents,
                        enabled: body.enabled,
                        tenant_id: body.rule.tenant_id,
                    },
                )
            ).data,
        ['/staff/approval-rules'],
    );
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    const columns: ListColumnDef<ApprovalRule>[] = [
        {
            key: 'action',
            label: 'Action',
            cell: (r) => <span className="font-mono text-xs">{r.action}</span>,
            locked: 'start',
        },
        {
            key: 'scope',
            label: 'Scope',
            cell: (r) => (r.tenant_id ? r.tenant_id : 'platform default'),
        },
        {
            key: 'threshold',
            label: 'Needs approval from',
            noTruncate: true,
            cell: (r) =>
                editable ? (
                    <form
                        method="post"
                        className="flex items-center gap-2"
                        onSubmit={(e) => {
                            e.preventDefault();
                            // BM-46 §2.7: integer cents from the money input; empty means no threshold
                            const cents =
                                r.id in drafts
                                    ? drafts[r.id]
                                    : r.threshold_cents;
                            put.mutate(
                                {
                                    rule: r,
                                    threshold_cents: cents,
                                    enabled: r.enabled,
                                },
                                { onError },
                            );
                        }}
                    >
                        <MoneyInput
                            aria-label={`${r.action} threshold (USD)`}
                            className="w-full md:w-40"
                            cents={
                                r.id in drafts
                                    ? drafts[r.id]
                                    : r.threshold_cents
                            }
                            onChange={(cents) =>
                                setDrafts({ ...drafts, [r.id]: cents })
                            }
                        />
                        <Button type="submit" size="sm" variant="outline">
                            Save
                        </Button>
                    </form>
                ) : r.threshold_cents === null ? (
                    'always'
                ) : (
                    formatMoney(r.threshold_cents)
                ),
        },
        {
            key: 'enabled',
            label: 'Enabled',
            cell: (r) =>
                editable ? (
                    <Switch
                        aria-label={`${r.action} enabled`}
                        checked={r.enabled}
                        onCheckedChange={(v) =>
                            put.mutate(
                                {
                                    rule: r,
                                    threshold_cents: r.threshold_cents,
                                    enabled: v,
                                },
                                { onError },
                            )
                        }
                    />
                ) : r.enabled ? (
                    'yes'
                ) : (
                    'no'
                ),
        },
    ];
    return (
        <div>
            {/* BM-46 §2.9: leaving with unsaved changes asks first */}
            <UnsavedGuard
                dirty={Object.entries(drafts).some(
                    ([id, cents]) =>
                        (query.data?.data.items ?? []).find((r) => r.id === id)
                            ?.threshold_cents !== cents,
                )}
            />
            <PageHeader
                crumbs={[{ label: 'Approvals', href: '/approvals' }]}
                title="Approval thresholds"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Above the amount (or always, when empty) the action waits for a second person. The owning services still hold the thresholds they enforce today; this table is the staff view until they move here.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            {error ? (
                <p role="alert" className="text-destructive mb-3 text-sm">
                    {error}
                </p>
            ) : null}
            <ListBody query={query} columns={columns} rowKey={(r) => r.id} />
        </div>
    );
}
